import { Drawer } from "@components";
import dayjs from "dayjs";
import ComboFilters from "pages/Reports/ComboFilters";
import { MonthYearChart } from "pages/Reports/MonthYearChart";
import { getAllMonthsUntilNow } from "pages/Reports/Reports";
import { ReportsAPI } from "providers/ReportsAPI";
import { useEffect } from "react";
import { useState } from "react";
import { If } from "shared/components/control-statements";
import { useDragScroll } from "shared/hooks/useDragScroll";
import styles from "styles/pages/reports/reports.module.scss";

const buildTable = (data) => {
  const statuses = {
    phone: `ERA "APENAS TELEFONE"`,
    document: `ERA "APENAS CPF"`,
    email: `ERA "APENAS E-MAIL"`,
    phone_document: `ERA "TELEFONE + CPF"`,
    phone_email: `ERA "TELEFONE + E-MAIL"`,
    document_email: `ERA "CPF + E-MAIL"`,
  };

  const rows = [
    "phone",
    "document",
    "email",
    "phone_document",
    "phone_email",
    "document_email",
  ];

  const columns = {
    complete: `VIROU "COMPLETAMENTE IDENTIFICADO"`,
    phone_document: `VIROU "TELEFONE + CPF"`,
    phone_email: `VIROU "TELEFONE + E-MAIL"`,
    document_email: `VIROU "CPF + E-MAIL"`,
  };

  const table = rows.map((row) => {
    return {
      alias: row,
      status: statuses[row],
      values: Object.entries(columns).map(([columnKey, columnLabel]) => {
        return {
          tuple: `${row}__${columnKey}`,
          label: columnLabel,
          value: data?.[`${row}__${columnKey}`] || 0,
        };
      }),
    };
  });

  return table;
};

export default function TransitionsIdentificationGroups({ data }) {
  const [loading, setLoading] = useState(false);
  const [loadingHistoric, setLoadingHistoric] = useState(false);
  const [rawData, setRawData] = useState(null);

  const getShade = (value, max) => {
    if (!value || +value === 0) {
      return {
        background: "#1e1e1f",
        color: "#808080",
      };
    }

    const intensity = Math.min(Math.max(value / max, 0), 1);
    const alpha = 0.6 + intensity * 1;

    return {
      background: `rgba(97, 97, 255, ${intensity})`,
      color: `rgba(255, 255, 255, ${alpha})`,
    };
  };

  const [hovered, setHovered] = useState(null);
  const [selectedData, setSelectedData] = useState(null);

  const months = getAllMonthsUntilNow(false, 6, 2025);

  const [selectedMonth, setSelectedMonth] = useState(months[0]);

  const { scrollRef, onMouseDown } = useDragScroll();

  const fetchHistory = async ({ year }) => {
    setLoadingHistoric(true);
    const provider = new ReportsAPI();
    const results = await provider.customerHistory("transitions", year, null);
    const rows = Array(12).fill(0);

    results.forEach((result, index) => {
      rows[index] = result?.[selectedData.alias] || 0;
    });

    setLoadingHistoric(false);
    return rows;
  };

  useEffect(() => {
    setRawData(data);
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const provider = new ReportsAPI();
      const response = await provider.customerTransitions(
        selectedMonth.year,
        selectedMonth.month
      );
      setRawData(response);
      setLoading(false);
    };

    fetchData();
  }, [selectedMonth]);

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>
            Transições entre grupos de identificação
          </p>
          <span
            className={styles.info}
            title="Quantidade de clientes que transacionou de um grupo de identificação para outro."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
        <ComboFilters
          options={months}
          onSelect={(option) => setSelectedMonth(option)}
          selectedOption={selectedMonth}
        />
      </div>

      <div className={styles.hairline} />

      <div
        className={styles.tableTransitions}
        onMouseDown={onMouseDown}
        ref={scrollRef}
      >
        <table>
          <thead>
            <tr>
              <th
                style={{
                  width: "20%",
                  minWidth: 300,
                  backgroundColor: "#1F1F1F",
                  verticalAlign: "middle",
                  color: "#4C4C4C",
                }}
              >
                <i className="far fa-arrow-turn-right" />
              </th>
              <th style={{ width: "20%" }}>
                VIROU "Completamente identificado"
              </th>
              <th style={{ width: "20%" }}>VIROU "Telefone + CPF"</th>
              <th style={{ width: "20%" }}>VIROU "Telefone + E-mail"</th>
              <th style={{ width: "20%" }}>VIROU "CPF + E-mail"</th>
            </tr>
          </thead>
          <tbody>
            {buildTable(rawData).map((row) => (
              <tr key={row.status}>
                <td className={styles.status} style={{ width: "15%" }}>
                  {row.status}
                </td>
                {row.values.map(({ tuple, label, value }, idx) => {
                  const allValues = buildTable(rawData).flatMap(
                    (item) => item.values
                  );

                  const sameColunRow = (str) => {
                    const [before, after] = str.split("__");
                    return before === after;
                  };

                  const maxItem = allValues
                    .filter((item) => !sameColunRow(item.tuple))
                    .reduce((max, item) => {
                      return Number(item.value) > Number(max.value)
                        ? item
                        : max;
                    });

                  let style;

                  if (sameColunRow(tuple)) {
                    style = {
                      background: "transparent",
                      color: "#808080",
                    };
                  } else style = getShade(value, Number(maxItem.value));

                  return (
                    <td
                      key={idx}
                      style={{
                        backgroundColor: style.background,
                        color: style.color,
                        width: "20%",
                        ...(value && { cursor: "pointer" }),
                        ...(value &&
                          tuple === hovered?.alias && {
                            filter: "brightness(1.1)",
                          }),
                      }}
                      onMouseEnter={() => {
                        setHovered({
                          alias: `${tuple}`,
                          name: `${row.status}`,
                        });
                      }}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => {
                        if (value && !sameColunRow(tuple))
                          setSelectedData({
                            alias: `${tuple}`,
                            name: `${row.status} E ${label}`,
                          });
                      }}
                    >
                      <If cond={sameColunRow(tuple)}>---</If>
                      <If cond={!sameColunRow(tuple)}>{value || "---"}</If>

                      <div
                        className={styles.chart}
                        style={{
                          ...(value &&
                            tuple === hovered?.alias &&
                            !sameColunRow(tuple) && {
                              transform: "translateY(0)",
                              opacity: 1,
                            }),
                        }}
                      >
                        <i className="far fa-chart-waterfall" />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Drawer
        visible={selectedData}
        onClose={() => setSelectedData(null)}
        withoutTransition
        direction="full-center"
        showCloseButton={false}
      >
        <MonthYearChart
          selectedData={selectedData}
          enableMonth={false}
          loading={loadingHistoric}
          onClose={() => setSelectedData(false)}
          onFetchData={(...params) => fetchHistory(...params)}
          startOn2025
        />
      </Drawer>
    </div>
  );
}
